<script setup lang="ts">
import axios from 'axios'
import {
  Clock,
  MapPin,
  MessageSquareReply,
  Sparkles,
  Loader2,
  Check,
  X,
  ExternalLink,
  Receipt,
  Gift
} from 'lucide-vue-next'
import type { DeliveryReview } from '@/types/reviewPlatforms'
import {
  NEGATIVE_RATING_THRESHOLD,
  DELIVEROO_ORDER_URL_PREFIX,
  UBEREATS_ORDER_URL_PREFIX,
  REPLY_DEADLINE_DAYS
} from '@/types/reviewPlatforms'
import ReviewPlatformRating from './ReviewPlatformRating.vue'

const props = defineProps<{
  review: DeliveryReview
}>()

const emit = defineEmits<{
  replied: [reviewId: number, responseText: string, compensationAmount: number | null]
}>()

const { t, locale } = useI18n()
const { merchantLogo, user } = useLocalStorage()
const notification = useNotification()

const isReplying = ref(false)
const replyText = ref('')
const isGenerating = ref(false)
const isSaving = ref(false)
const selectedRewardAmount = ref<number | null>(null)

const DELIVEROO_REWARD_AMOUNTS = [1.5, 2.5, 5, 7.5, 10, 15, 20] as const
const UBEREATS_REWARD_AMOUNTS = [5, 10, 18] as const
const DEFAULT_AI_TONE = 'professional'

const platformSlug = computed(
  () => props.review.storeReviewPlatform?.platform?.slug ?? props.review.platformSlug ?? 'deliveroo'
)
const isNegative = computed(() => props.review.rating <= NEGATIVE_RATING_THRESHOLD)
const hasReply = computed(() => Boolean(props.review.ownerResponseText && props.review.ownerResponseText.length > 0))
const canReply = computed(() => props.review.reviewManager?.hasReplyFeature ?? false)

const replyDeadlineDays = computed(() => REPLY_DEADLINE_DAYS[platformSlug.value] ?? 60)
const replyExpired = computed(() => {
  if (!canReply.value || hasReply.value) return false
  const reviewDate = new Date(props.review.date)
  const deadline = new Date(reviewDate.getTime() + replyDeadlineDays.value * 24 * 60 * 60 * 1000)
  return new Date() > deadline
})
const formattedDate = computed(() => {
  const d = new Date(props.review.date)
  return d.toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' })
})

const formattedOrderTotal = computed(() => {
  const total = props.review.order?.orderTotal
  return total == null ? null : formatCurrency(total)
})

const formattedOrderDate = computed(() => {
  const d = props.review.order?.deliveredAt
  if (!d) return null
  return new Date(d).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' })
})

const orderUrl = computed(() => {
  const uuid = props.review.order?.orderUuid
  if (!uuid) return null
  if (platformSlug.value === 'deliveroo') return `${DELIVEROO_ORDER_URL_PREFIX}/${uuid}`
  if (platformSlug.value === 'ubereats') return `${UBEREATS_ORDER_URL_PREFIX}/${uuid}`
  return null
})

const rewardAmounts = computed(() =>
  platformSlug.value === 'ubereats' ? UBEREATS_REWARD_AMOUNTS : DELIVEROO_REWARD_AMOUNTS
)

// useNumber().formatCurrency hardcodes EUR; delivery orders can be in GBP/USD so we keep a local helper.
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: props.review.order?.currencyCode ?? 'EUR'
  }).format(amount)
}
function openReply() {
  replyText.value = ''
  isReplying.value = true
}

function cancelReply() {
  isReplying.value = false
  replyText.value = ''
  selectedRewardAmount.value = null
}

async function generateWithAi() {
  isGenerating.value = true
  try {
    const { data } = await axios.post(`/reviews/${props.review.id}/ai-reply`, {
      tone: DEFAULT_AI_TONE
    })
    replyText.value = data.data?.text ?? data.text ?? ''
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.reviews.aiGenerateError') })
  } finally {
    isGenerating.value = false
  }
}

async function publishReply() {
  if (!replyText.value.trim()) return
  isSaving.value = true
  try {
    const payload = { response: replyText.value, compensationAmount: selectedRewardAmount.value }
    await axios.post(`/reviews/${props.review.id}/reply`, payload)
    notification.create('success', { text: t('pages.reviewPlatforms.reviews.replyPublished') })
    emit('replied', props.review.id, replyText.value, selectedRewardAmount.value)
    cancelReply()
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.reviews.replyError') })
  } finally {
    isSaving.value = false
  }
}

function selectRewardAmount(amount: number) {
  selectedRewardAmount.value = amount
}

function clearReward() {
  selectedRewardAmount.value = null
}
</script>

<template>
  <article class="rounded-xl border border-border bg-card transition-colors hover:border-muted-foreground/30">
    <div class="flex gap-4 p-5">
      <img
        :src="`/images/reviews/${platformSlug}.svg`"
        :alt="platformSlug"
        class="size-10 shrink-0 rounded-lg border border-border bg-background object-contain p-1"
      />

      <div class="flex-1 min-w-0">
        <header class="flex flex-wrap items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-semibold text-foreground">
                {{ review.name || t('pages.reviewPlatforms.reviews.anonymous') }}
              </span>
              <ReviewPlatformRating :rating="review.rating" />
            </div>
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span class="inline-flex items-center gap-1">
                <Clock class="size-3.5" />
                {{ formattedDate }}
              </span>
              <span class="inline-flex items-center gap-1">
                <MapPin class="size-3.5" />
                {{ review.store.name }}
              </span>
            </div>
          </div>

          <BaseChip v-if="hasReply" color="green" class="shrink-0" :text="t('pages.reviewPlatforms.reviews.replied')">
            <template #leading>
              <Check class="size-3.5" />
            </template>
          </BaseChip>
        </header>

        <p v-if="review.text" class="mt-3 text-sm leading-relaxed text-foreground">
          {{ review.text }}
        </p>
        <p v-else class="mt-3 text-sm italic text-muted-foreground">
          {{ t('pages.reviewPlatforms.reviews.noText') }}
        </p>

        <div v-if="review.order" class="mt-3 flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger as-child>
              <Button variant="outline" size="sm">
                <Receipt class="text-muted-foreground" />
                <span v-if="formattedOrderTotal" class="font-semibold">{{ formattedOrderTotal }}</span>
                <span v-if="formattedOrderDate" class="text-muted-foreground">· {{ formattedOrderDate }}</span>
                <span v-if="review.order.items?.length" class="text-muted-foreground">
                  · {{ t('pages.reviewPlatforms.reviews.order.itemsCount', { count: review.order.items.length }) }}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-80 p-0" align="start">
              <div class="border-b border-border p-3">
                <p class="text-xs font-semibold text-foreground">
                  {{ t('pages.reviewPlatforms.reviews.order.title') }}
                </p>
                <p v-if="formattedOrderDate" class="mt-0.5 text-xs text-muted-foreground">
                  {{ formattedOrderDate }}
                </p>
              </div>
              <ul v-if="review.order.items?.length" class="divide-y divide-border max-h-72 overflow-y-auto">
                <li
                  v-for="(item, idx) in review.order.items"
                  :key="idx"
                  class="flex items-start justify-between gap-3 px-3 py-2 text-xs"
                >
                  <div class="min-w-0 flex-1">
                    <span class="font-medium text-foreground">{{ item.quantity }}×</span>
                    <span class="ml-1 text-foreground">{{ item.name }}</span>
                  </div>
                  <span class="shrink-0 font-medium text-foreground">{{ formatCurrency(item.price) }}</span>
                </li>
              </ul>
              <div
                v-if="formattedOrderTotal"
                class="flex items-center justify-between border-t border-border bg-muted/50 px-3 py-2"
              >
                <span class="text-xs font-semibold text-foreground">{{
                  t('pages.reviewPlatforms.reviews.order.total')
                }}</span>
                <span class="text-xs font-bold text-foreground">{{ formattedOrderTotal }}</span>
              </div>
              <a
                v-if="orderUrl"
                :href="orderUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-1.5 border-t border-border bg-background px-3 py-2 text-xs font-medium text-primary hover:bg-muted/50"
              >
                <ExternalLink class="size-3.5" />
                {{ t('pages.reviewPlatforms.reviews.order.viewOnPlatform') }}
              </a>
            </PopoverContent>
          </Popover>

          <BaseChip
            v-if="hasReply && review.compensationAmount"
            color="violet"
            :text="
              t('pages.reviewPlatforms.reviews.order.compensationGiven', {
                amount: formatCurrency(review.compensationAmount)
              })
            "
          >
            <template #leading>
              <Gift class="size-3.5" />
            </template>
          </BaseChip>
        </div>

        <div v-if="hasReply && !isReplying" class="mt-4 flex gap-3 rounded-lg bg-muted/50 p-4">
          <img
            :src="merchantLogo"
            alt=""
            class="size-8 shrink-0 rounded-full border border-border bg-background object-contain"
          />
          <div class="flex-1 min-w-0">
            <p class="text-xs font-semibold text-foreground">
              {{ review.store.name
              }}<span v-if="user?.jobTitle" class="text-muted-foreground"> · {{ user.jobTitle }}</span>
            </p>
            <p class="mt-1 text-sm text-foreground">{{ review.ownerResponseText }}</p>
          </div>
        </div>

        <div v-if="!isReplying" class="mt-4 flex flex-wrap items-center gap-2">
          <Button v-if="canReply && !hasReply && !replyExpired" variant="default-primary" size="sm" @click="openReply">
            <MessageSquareReply />
            {{ t('pages.reviewPlatforms.reviews.reply') }}
          </Button>
          <Badge v-if="replyExpired" variant="secondary" class="gap-1.5 text-muted-foreground">
            <Clock class="size-3" />
            {{ t('pages.reviewPlatforms.reviews.replyDeadlineExpired', { days: replyDeadlineDays }) }}
          </Badge>
          <Button v-if="review.sourceUrl" variant="ghost" size="sm" as-child>
            <a :href="review.sourceUrl" target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              {{ t('pages.reviewPlatforms.reviews.viewOnPlatform') }}
            </a>
          </Button>
        </div>
      </div>
    </div>

    <Transition name="expand">
      <div v-if="isReplying" class="border-t border-border bg-muted/30 p-5">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-foreground">
            {{ t('pages.reviewPlatforms.reviews.yourReply') }}
          </h3>
          <Button variant="outline" size="sm" :disabled="isGenerating" @click="generateWithAi">
            <Loader2 v-if="isGenerating" class="animate-spin" />
            <Sparkles v-else />
            {{ t('pages.reviewPlatforms.reviews.generateWithAi') }}
          </Button>
        </div>

        <Textarea
          v-model="replyText"
          :placeholder="t('pages.reviewPlatforms.reviews.replyPlaceholder')"
          class="mt-3 min-h-32 bg-background"
        />

        <div class="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5">
          <span class="text-xs font-medium text-muted-foreground shrink-0">
            {{ t('pages.reviewPlatforms.reviews.rewards.compensationLabel') }}
          </span>
          <div class="flex flex-wrap items-center gap-1.5">
            <Button :variant="selectedRewardAmount === null ? 'default' : 'outline'" size="sm" @click="clearReward">
              {{ t('pages.reviewPlatforms.reviews.rewards.none') }}
            </Button>
            <Button
              v-for="amount in rewardAmounts"
              :key="amount"
              :variant="selectedRewardAmount === amount ? 'default-primary' : 'outline'"
              size="sm"
              @click="selectRewardAmount(amount)"
            >
              {{ formatCurrency(amount) }}
            </Button>
          </div>
          <BaseChip
            v-if="isNegative"
            color="orange"
            class="ml-auto shrink-0"
            :text="t('pages.reviewPlatforms.reviews.rewards.recommended')"
          />
        </div>

        <Alert class="mt-4">
          <AlertDescription class="text-xs">
            {{ t('pages.reviewPlatforms.reviews.replyIrreversibleWarning') }}
          </AlertDescription>
        </Alert>

        <div class="mt-5 flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" size="sm" @click="cancelReply">
            <X />
            {{ t('pages.reviewPlatforms.reviews.cancel') }}
          </Button>
          <Button variant="default-primary" size="sm" :disabled="!replyText.trim() || isSaving" @click="publishReply">
            <Loader2 v-if="isSaving" class="animate-spin" />
            <MessageSquareReply v-else />
            {{ t('pages.reviewPlatforms.reviews.publishReply') }}
          </Button>
        </div>
      </div>
    </Transition>
  </article>
</template>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition:
    opacity 0.18s ease,
    max-height 0.25s ease;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}
.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  /* Large enough for the reply form (textarea + reward picker + actions). CSS transitions require a fixed upper bound on max-height. */
  max-height: 800px;
}
</style>
